import React, { useCallback, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../utils/firebase';
import { type Currency, getSubscribeButtonText } from '../utils/pricing';

// Initialize Stripe
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

interface EmbeddedCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  billingPeriod?: 'monthly' | 'yearly';
  currency?: Currency;
}

const EmbeddedCheckoutModal: React.FC<EmbeddedCheckoutModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  billingPeriod = 'monthly',
  currency = 'EUR'
}) => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const fetchClientSecret = useCallback(async () => {
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }

    // Get Firebase ID token for authentication
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    const token = await currentUser.getIdToken();

    // In production (Vercel), use relative URL. In dev, use localhost
    const apiUrl = import.meta.env.DEV ? 'http://localhost:3001' : '';
    
    const response = await fetch(`${apiUrl}/api/stripe/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        email: user.email,
        embedded: true, // Request embedded mode
        billingPeriod: billingPeriod, // Send selected billing period
        currency: currency // Send detected currency
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data.clientSecret;
  }, [user, billingPeriod, currency]);

  const handleComplete = useCallback(() => {
    // Payment completed successfully
    // Set premium status immediately so it's active when page reloads
    localStorage.setItem('DEV_FORCE_PREMIUM', 'true');
    localStorage.setItem('PREMIUM_ACTIVATED_AT', new Date().toISOString());
    
    // Allow the Stripe redirect without showing "Leave site?" warning
    (window as any).ALLOW_CHECKOUT_NAVIGATION = true;
    
    onSuccess();
    // Note: Stripe Embedded Checkout will automatically redirect to return_url
  }, [onSuccess]);

  if (!isOpen) return null;

  if (!stripePromise) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md">
          <p className="text-red-500">Stripe not configured. Check VITE_STRIPE_PUBLISHABLE_KEY.</p>
          <button 
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md">
          <p className="text-yellow-500">Please sign in first to upgrade to Premium.</p>
          <button 
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">🚀 Upgrade to Premium</h2>
              <p className="text-blue-100 text-sm">{getSubscribeButtonText(billingPeriod, currency)} - Cancel anytime</p>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Checkout Container */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
          {error ? (
            <div className="text-red-500 p-4 text-center">
              <p>{error}</p>
              <button 
                onClick={() => setError(null)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div id="checkout" className="min-h-[400px]">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  fetchClientSecret,
                  onComplete: handleComplete
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div>

        {/* Features reminder */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            ✓ Cloud Sync · ✓ PDF Export · ✓ Unlimited Setlists · ✓ Jam Sessions
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedCheckoutModal;
